/**
 * @file The one error shape the chunked-attachment reader throws, so a caller
 * can tell "these bytes did not verify" from "a chunk is missing" from "this
 * file is too large for the browser path" from "the user stopped it" and say
 * something true to the user.
 * Consumers: `readChunkedAttachmentVersion`, `verifyChunkBytes`, and the
 * download and preview funnels in `react/src/use/attachments/` and
 * `react/src/components/rendering/attachments/`.
 * Invariants: `message` is user-presentable on its own — the funnels surface it
 * verbatim — and `reason` is what code branches on, never the message text.
 * `cancelled` is the one reason that is not a failure; a funnel reports it
 * neutrally, never as an error.
 */

/**
 * Why a chunked read ended without returning bytes.
 *
 * - `manifestPathMismatch` — the manifest path given does not belong to the
 *   version being read. A manifest path is derived entirely from the version id,
 *   so this means the request mixed two versions' fields, never a server state.
 * - `manifestUnreadable` — the manifest object could not be fetched.
 * - `manifestRefused` — the manifest parsed but violates the contract, which
 *   includes a `formatVersion` this client does not know.
 * - `chunkMissing` — a chunk the manifest names could not be read. On a
 *   committed version this is an integrity failure, never an expected race,
 *   because a writer commits only after every chunk is readable. Retryable.
 * - `integrity` — fetched bytes disagree with the manifest: a chunk digest, a
 *   chunk length, or the assembled total.
 * - `encodingUnsupported` — the manifest declares a chunk encoding no browser
 *   can decode, today meaning `zstd`.
 * - `tooLarge` — the version exceeds what this browser path can assemble.
 * - `cancelled` — the caller's `AbortSignal` fired before the read finished.
 *   Nothing is wrong with the version; the caller asked to stop. `cause` is the
 *   signal's `reason`.
 */
export type ChunkedAttachmentErrorReason =
  | 'manifestPathMismatch'
  | 'manifestUnreadable'
  | 'manifestRefused'
  | 'chunkMissing'
  | 'integrity'
  | 'encodingUnsupported'
  | 'tooLarge'
  | 'cancelled';

/** Thrown by the chunked reader, and by nothing else, whenever a read returns no bytes. */
export class ChunkedAttachmentError extends Error {
  readonly reason: ChunkedAttachmentErrorReason;

  constructor(reason: ChunkedAttachmentErrorReason, message: string, cause?: unknown) {
    super(message, cause === undefined ? undefined : { cause });
    this.name = 'ChunkedAttachmentError';
    this.reason = reason;
  }
}
